import { RhButton, RhCard, RhDivider, RhScrollbar } from "@rhythm-ui/react";
import React, { useEffect } from "react";

import { JobFilterParamType } from "@/types/job";

import {
  locationPreferenceOptions,
  locationPreferenceValues,
} from "@/constants/candidates";
import mixPanelEventName from "@/constants/mixPanelEventName";

// import useIndustryList from "@/hooks/dataFetch/useIndustryList";
// import usePublicIndustryList from "@/hooks/publicDataFetch/usePublicIndustryList";

// import { propToFormatCityLabelWithRhSelect } from "@/components/component-helper/propToFormatCityLabelWithRhSelect";
import DataSelect from "@/components/dataInput/DataSelect";
import {
  citySearchApiCall,
  jobRoleSearchApiCall,
  skillSearchApiCall,
} from "@/components/dataInputApiCalls";
import {
  jobRoleSearchPublicApiCall,
  skillSearchPublicApiCall,
} from "@/components/dataInputPublicApiCalls";
import EventTracker from "@/components/eventTracker/EventTracker";
import FilterHeader from "@/components/forms/FilterHeader";
import MinMaxFilterInput from "@/components/forms/MinMaxFilterInput";
import { CheckboxInput } from "@/components/input";

interface JobFilterType {
  filterJobsOn: JobFilterParamType;
  setFilterJobsOn: React.Dispatch<React.SetStateAction<JobFilterParamType>>;
  onAllFilterClear: () => void;
  isPagePublic: boolean | undefined;
}

const JobFilter = ({
  filterJobsOn,
  setFilterJobsOn,
  onAllFilterClear,
  isPagePublic,
}: JobFilterType) => {
  useEffect(() => {
    if (filterJobsOn.work_preference_new__selected?.length === 0) {
      setFilterJobsOn((state) => {
        const { ...rest } = state;
        delete rest.work_preference_new__selected;
        delete rest.city_location_new__selected;
        return rest;
      });
    }

    if (filterJobsOn.skills__selected?.length === 0) {
      setFilterJobsOn((state) => {
        const { ...rest } = state;
        delete rest.skills__selected;
        return rest;
      });
    }

    if (filterJobsOn.company__selected?.length === 0) {
      setFilterJobsOn((state) => {
        const { ...rest } = state;
        delete rest.company__selected;
        return rest;
      });
    }
  }, [
    filterJobsOn.work_preference_new__selected,
    filterJobsOn.company__selected,
    filterJobsOn.skills__selected,
  ]);

  const handleClearFilterKey = (keys: (keyof JobFilterParamType)[]) => {
    setFilterJobsOn((state) => {
      const { ...rest } = state;
      keys.forEach((key) => {
        delete rest[key];
      });
      return rest;
    });
  };

  // const industries = isPagePublic ? usePublicIndustryList() : useIndustryList();

  // const formattedIndustryList = useMemo(() => {
  //   return industries.map((industry) => ({
  //     label: industry.name,
  //     value: industry.id.toString(),
  //   }));
  // }, [industries]);

  return (
    <RhCard className=" flex flex-col border mb-tm2">
      <div className="px-tsm py-tpico flex items-center justify-between h-[48px] border-b border-gray-100">
        <h4>Filters</h4>
        <RhButton
          className={`button-tertiary button-sm`}
          onClick={onAllFilterClear}
        >
          Clear All
        </RhButton>
      </div>

      <div className="flex-1 overflow-auto">
        <RhScrollbar style={{ height: "100%" }}>
          <div className="px-tsm py-tpico">
            <FilterHeader
              title={"Job Role"}
              handleClearFilterKey={handleClearFilterKey}
              values={filterJobsOn}
              valueKey={["job_role_details__selected"]}
            />
            <EventTracker
              name={mixPanelEventName.CHANGE_FILTER}
              properties={{ filterUsed: "Job Role" }}
            >
              {({ triggerEvent }) => (
                <DataSelect
                  className="mb-tsm"
                  apiCall={
                    isPagePublic
                      ? jobRoleSearchPublicApiCall
                      : jobRoleSearchApiCall
                  }
                  values={filterJobsOn}
                  valueKey={"job_role_details__selected"}
                  setReactState={setFilterJobsOn}
                  placeholder="Search job role"
                  __onOptionAdd={triggerEvent}
                />
              )}
            </EventTracker>
          </div>

          <div className="mb-tmini">
            <RhDivider />
          </div>

          <div className="px-tsm py-tpic">
            <FilterHeader
              title={"Skills"}
              handleClearFilterKey={handleClearFilterKey}
              values={filterJobsOn}
              valueKey={["skills__selected"]}
            />

            <EventTracker
              name={mixPanelEventName.CHANGE_FILTER}
              properties={{ filterUsed: "Skills" }}
            >
              {({ triggerEvent }) => (
                <DataSelect
                  apiCall={
                    isPagePublic ? skillSearchPublicApiCall : skillSearchApiCall
                  }
                  values={filterJobsOn}
                  valueKey={"skills__selected"}
                  setReactState={setFilterJobsOn}
                  isMulti
                  placeholder="Search Skills"
                  __onOptionAdd={triggerEvent}
                  className="mb-tsm"
                />
              )}
            </EventTracker>
          </div>

          <div className="mb-tmini">
            <RhDivider />
          </div>

          <div className="px-tsm py-tpico">
            <EventTracker
              name={mixPanelEventName.CHANGE_FILTER}
              properties={{ filterUsed: "Experience" }}
            >
              {({ triggerEvent }) => (
                <MinMaxFilterInput
                  label="Experience"
                  subLabel="(y)"
                  filterOn={filterJobsOn}
                  setFilterOn={setFilterJobsOn}
                  minKey={"min_exp"}
                  maxKey="max_exp"
                  handleClearFilterKey={handleClearFilterKey}
                  __onInputChange={triggerEvent}
                />
              )}
            </EventTracker>
          </div>
        </RhScrollbar>
      </div>

      <div className="mb-tmini">
        <RhDivider />
      </div>

      <div className="px-tsm py-tpico">
        <FilterHeader
          title={"Location"}
          handleClearFilterKey={handleClearFilterKey}
          values={filterJobsOn}
          valueKey={[
            "work_preference_new__selected",
            "city_location_new__selected",
          ]}
        />

        <EventTracker
          name={mixPanelEventName.CHANGE_FILTER}
          properties={{ filterUsed: "Location Type" }}
        >
          {({ triggerEvent }) => (
            <CheckboxInput
              values={filterJobsOn}
              setReactState={setFilterJobsOn}
              options={locationPreferenceOptions}
              valueKey="work_preference_new__selected"
              __onChange={triggerEvent}
            />
          )}
        </EventTracker>

        {(filterJobsOn.work_preference_new__selected?.includes(
          locationPreferenceValues.Onsite
        ) ||
          filterJobsOn.work_preference_new__selected?.includes(
            locationPreferenceValues.Hybrid
          )) && (
          <>
            <div className="mt-tsm"></div>

            <EventTracker
              name={mixPanelEventName.CHANGE_FILTER}
              properties={{ filterUsed: "City location" }}
            >
              {({ triggerEvent }) => (
                <DataSelect
                  apiCall={citySearchApiCall}
                  values={filterJobsOn}
                  valueKey={"city_location_new__selected"}
                  setReactState={setFilterJobsOn}
                  isMulti
                  __onOptionAdd={triggerEvent}
                  className="mb-tsm"
                />
              )}
            </EventTracker>
          </>
        )}
      </div>
    </RhCard>
  );
};

export default JobFilter;
